/*
  GpProviderNumberBackfill — Reports → GP Provider Nos tab.

  Iterates every live GP record missing a provider_number, runs the
  AI lookup in parallel (capped concurrency so we don't rate-limit
  the LLM), shows a review table with per-row suggestion +
  confidence + sources, lets the operator tick which to accept, and
  bulk-persists the chosen values via a single POST.

  Human-in-the-loop by design — the AI isn't always right about
  which "Dr Smith" is which, so we never auto-save.
*/
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Check, ExternalLink, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CONCURRENCY = 4;

export default function GpProviderNumberBackfill() {
  const [loading, setLoading] = useState(true);
  const [gps, setGps] = useState([]);
  // { [gp_id]: { status, suggestion, confidence, sources, picked, error } }
  const [state, setState] = useState({});
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/letters/gps/missing-provider-numbers");
      setGps(r.data?.gps || []);
      setState({});
    } catch (e) {
      toast.error("Could not load GPs", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return gps;
    return gps.filter((g) => {
      const haystack = [
        g.title, g.first_name, g.last_name, g.clinic,
        g.suburb, g.state, g.postcode,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [gps, filter]);

  async function lookupOne(gp) {
    setState((s) => ({ ...s, [gp.id]: { ...(s[gp.id] || {}), status: "running", error: null } }));
    try {
      const r = await api.post(`/letters/gps/${gp.id}/lookup-provider-number`);
      const suggestion = r.data?.provider_number || "";
      const confidence = r.data?.confidence || null;
      const sources = r.data?.sources || [];
      setState((s) => ({
        ...s,
        [gp.id]: {
          status: suggestion ? "done" : "no-result",
          suggestion,
          confidence,
          sources,
          picked: !!suggestion && confidence !== "low",
        },
      }));
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "lookup failed";
      setState((s) => ({ ...s, [gp.id]: { status: "error", error: detail } }));
    }
  }

  async function lookupAll() {
    setRunning(true);
    try {
      // Only lookup rows that are currently visible AND haven't been
      // successfully looked up yet. Lets the operator filter first,
      // then run only the subset they care about.
      const queue = visible.filter(
        (g) => !state[g.id] || state[g.id].status === "error",
      );
      if (!queue.length) {
        toast.info("Nothing to look up — all visible rows already have a result.");
        return;
      }
      // Simple fixed-size concurrency pool so we don't hammer the LLM.
      let cursor = 0;
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, queue.length) }).map(
          async () => {
            while (cursor < queue.length) {
              const idx = cursor++;
              await lookupOne(queue[idx]);
            }
          },
        ),
      );
      toast.success("AI lookup complete — review and tick the rows to save.");
    } finally {
      setRunning(false);
    }
  }

  function togglePicked(gpId) {
    setState((s) => ({ ...s, [gpId]: { ...(s[gpId] || {}), picked: !s[gpId]?.picked } }));
  }

  function editSuggestion(gpId, value) {
    setState((s) => ({
      ...s,
      [gpId]: {
        ...(s[gpId] || { status: "manual" }),
        suggestion: value,
        picked: value.trim().length > 0,
      },
    }));
  }

  async function saveSelected() {
    const items = Object.entries(state)
      .filter(([, v]) => v.picked && (v.suggestion || "").trim())
      .map(([gp_id, v]) => ({ gp_id, provider_number: v.suggestion.trim() }));
    if (!items.length) {
      toast.info("Tick at least one row with a provider number first.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/letters/gps/bulk-save-provider-numbers", { items });
      toast.success(`Saved ${r.data?.saved_count || 0} provider number(s)`);
      // Drop saved rows from the "missing" list without re-fetching.
      const saved = new Set(r.data?.saved || []);
      setGps((rows) => rows.filter((r) => !saved.has(r.id)));
      setState((s) => {
        const next = { ...s };
        saved.forEach((id) => delete next[id]);
        return next;
      });
    } catch (e) {
      toast.error("Bulk save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  const selectedCount = Object.values(state).filter(
    (v) => v.picked && (v.suggestion || "").trim(),
  ).length;

  return (
    <div className="space-y-5" data-testid="gp-provnum-backfill">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          GP directory maintenance
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
          GP Provider Nº backfill
        </h1>
        <p className="mt-2 text-sm text-zinc-600 max-w-3xl">
          Every GP referral letter is more useful to the receiving clinic when
          their Medicare Provider Number appears under their name. Use this
          tool to run the AI web lookup across every GP who's missing one, then
          review the suggestions before saving. Nothing is persisted until you
          tick the row and click <strong>Save selected</strong>.
        </p>
      </div>

      <Card className="p-4 sm:p-5 border border-zinc-200 shadow-none">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">
              Showing
            </div>
            <div className="text-sm text-zinc-900" data-testid="gp-provnum-count">
              <strong>{visible.length}</strong> of {gps.length} GPs missing a provider number
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-zinc-400" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by name, clinic, suburb…"
                className="pl-7 h-9 w-60 border-zinc-300"
                data-testid="gp-provnum-filter"
              />
            </div>
            <Button
              variant="outline"
              onClick={refresh}
              disabled={loading}
              className="border-zinc-300 h-9"
              data-testid="gp-provnum-refresh"
            >
              <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
            <Button
              onClick={lookupAll}
              disabled={running || !visible.length}
              className="bg-zinc-950 text-white hover:bg-zinc-800 h-9"
              data-testid="gp-provnum-lookup-all"
            >
              {running
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Looking up…</>
                : <>Lookup all visible</>}
            </Button>
            <SaveSelectedButton
              selectedCount={selectedCount}
              saving={saving}
              onClick={saveSelected}
            />
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading GPs…
        </div>
      ) : visible.length === 0 ? (
        <Card className="p-8 border border-zinc-200 shadow-none text-center text-sm text-zinc-500">
          <Check className="h-6 w-6 mx-auto mb-2 text-emerald-600" />
          Every GP in your directory has a provider number recorded. 🎉
        </Card>
      ) : (
        <Card className="border border-zinc-200 shadow-none overflow-hidden">
          <table className="w-full text-sm" data-testid="gp-provnum-table">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 w-8"></th>
                <th className="text-left px-3 py-2">GP</th>
                <th className="text-left px-3 py-2">Clinic</th>
                <th className="text-left px-3 py-2 w-56">Suggested Provider Nº</th>
                <th className="text-left px-3 py-2 w-28">Confidence</th>
                <th className="text-left px-3 py-2 w-20">Sources</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((gp) => {
                const s = state[gp.id] || {};
                const isRunning = s.status === "running";
                return (
                  <tr key={gp.id} className="border-t border-zinc-100 hover:bg-zinc-50/60">
                    <td className="px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={!!s.picked}
                        onChange={() => togglePicked(gp.id)}
                        disabled={!s.suggestion}
                        className="mt-1.5"
                        data-testid={`gp-provnum-pick-${gp.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-zinc-900">
                        {[gp.title, gp.first_name, gp.last_name].filter(Boolean).join(" ")}
                      </div>
                      <div className="text-2xs text-zinc-500">
                        {[gp.suburb, gp.state, gp.postcode].filter(Boolean).join(" ")}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top text-zinc-700">
                      {gp.clinic || "—"}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {isRunning ? (
                        <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                          <Loader2 className="h-3 w-3 animate-spin" /> looking up…
                        </div>
                      ) : s.status === "error" ? (
                        <div className="text-xs text-red-700">
                          Lookup failed — <button className="underline" onClick={() => lookupOne(gp)}>retry</button>
                          <div className="text-2xs text-zinc-500 mt-0.5">{s.error}</div>
                        </div>
                      ) : s.status === "no-result" ? (
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Enter manually"
                            value={s.suggestion || ""}
                            onChange={(e) => editSuggestion(gp.id, e.target.value)}
                            className="h-7 border-zinc-300"
                            data-testid={`gp-provnum-input-${gp.id}`}
                          />
                          <button
                            onClick={() => lookupOne(gp)}
                            className="text-2xs text-zinc-500 underline whitespace-nowrap"
                          >
                            retry
                          </button>
                        </div>
                      ) : s.suggestion ? (
                        <Input
                          value={s.suggestion}
                          onChange={(e) => editSuggestion(gp.id, e.target.value)}
                          className="h-7 border-zinc-300 font-mono-num"
                          data-testid={`gp-provnum-input-${gp.id}`}
                        />
                      ) : (
                        <button
                          onClick={() => lookupOne(gp)}
                          className="text-xs text-zinc-700 underline"
                          data-testid={`gp-provnum-lookup-row-${gp.id}`}
                        >
                          Lookup
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {s.confidence ? (
                        <ConfidenceBadge level={s.confidence} />
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {Array.isArray(s.sources) && s.sources.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          {s.sources.slice(0, 2).map((src, i) => (
                            <a
                              key={i}
                              href={src}
                              target="_blank"
                              rel="noreferrer"
                              className="text-2xs text-blue-700 hover:underline flex items-center gap-0.5"
                              title={src}
                            >
                              <ExternalLink className="h-2.5 w-2.5" />
                              link {i + 1}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}


function ConfidenceBadge({ level }) {
  const tone = level === "high" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
             : level === "medium" ? "bg-amber-50 text-amber-800 border-amber-200"
             : "bg-zinc-100 text-zinc-700 border-zinc-200";
  return (
    <span className={`inline-flex items-center uppercase tracking-wider text-2xs font-semibold border px-1.5 py-0.5 rounded ${tone}`}>
      {level}
    </span>
  );
}


function SaveSelectedButton({ selectedCount, saving, onClick }) {
  return (
    <Button
      onClick={onClick}
      disabled={!selectedCount || saving}
      className="bg-emerald-700 text-white hover:bg-emerald-800 h-9"
      data-testid="gp-provnum-save-selected"
      style={{
        opacity: !selectedCount && !saving ? 0.5 : 1,
        cursor: !selectedCount ? "not-allowed" : "pointer",
      }}
    >
      {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
      Save {selectedCount} selected
    </Button>
  );
}
